import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Filter, useFilter } from "@/partner/shared/components/Filter";
import { useInstancePaymentProviders } from "@/partner/entities/instance";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  ProductPurchaseStatusType,
  ProductPurchaseTypeType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";
import { buildPurchasesFilter } from "../model/buildPurchasesFilter";

// Статусы покупки: подписи уже есть в сиде (`purchases.status.*`), сам
// статус на бэкенде вычисляемый (refunded/disputed/expiredAt), поэтому
// enum-значение отдаём как есть в `filter.status`.
const STATUS_OPTIONS: {
  value: ProductPurchaseStatusType;
  labelKey: string;
}[] = [
  {
    value: ProductPurchaseStatusType.ActiveType,
    labelKey: "purchases.status.active",
  },
  {
    value: ProductPurchaseStatusType.ExpiredType,
    labelKey: "purchases.status.expired",
  },
  {
    value: ProductPurchaseStatusType.RefundedType,
    labelKey: "purchases.status.refunded",
  },
  {
    value: ProductPurchaseStatusType.DisputedType,
    labelKey: "purchases.status.disputed",
  },
];

// Подписи типа переиспользуем от продуктов (`products.type.*`) — тот же
// приём, что в RefundsList, вместо дублирующих ключей purchases.type.*.
const TYPE_OPTIONS: { value: ProductPurchaseTypeType; labelKey: string }[] = [
  { value: ProductPurchaseTypeType.PpvType, labelKey: "products.type.ppv" },
  {
    value: ProductPurchaseTypeType.SubscriptionType,
    labelKey: "products.type.subscription",
  },
];

export const PurchasesFilter: FC = () => {
  const { t } = useTranslation();
  const { filter } = useFilter();
  // Селект «Провайдер» предлагает только провайдеров развёртывания:
  // покупок через остальных на инстансе не бывает.
  const paymentProviders = useInstancePaymentProviders();

  // Статус и тип — обычные поля общей сетки фильтра (`selects`), как поиск и
  // даты: раньше они рисовались чипами в шапке карточки и раздел выглядел
  // иначе, чем Users/Links/Videos. Экспорт CSV живёт кнопкой в шапке
  // карточки — как `action` → `DownloadReportButton` в референсе.
  return (
    <Filter
      title={t("purchases.pageTitle")}
      paymentProviders={paymentProviders}
      action={
        <DownloadReportButton
          type={ReportTypeType.PurchasesType}
          filter={
            buildPurchasesFilter(filter) as Record<string, unknown> | undefined
          }
        />
      }
      selects={[
        {
          name: "status",
          label: t("purchases.filter.statusLabel"),
          options: STATUS_OPTIONS.map(({ value, labelKey }) => ({
            value,
            label: t(labelKey),
          })),
        },
        {
          name: "type",
          label: t("purchases.filter.typeLabel"),
          options: TYPE_OPTIONS.map(({ value, labelKey }) => ({
            value,
            label: t(labelKey),
          })),
        },
      ]}
      fields={{
        search: true,
        dateFrom: true,
        dateTo: true,
        productIds: true,
        providers: true,
        currency: true,
      }}
    />
  );
};
